import { z } from 'zod';
import { characterSummarySchema } from '../creation/character.js';
/**
 * Формы сообщений аутентификации (D8).
 *
 * Всё, что пересекает границу клиент-сервер, объявлено здесь и типы
 * выводятся отсюда же (CLAUDE.md, п. 6). Валидация — часть определения
 * процедуры, а не её тела (security-checklist.md, п. 7).
 */
const uuid = z.uuid();
/**
 * Языки профиля (D11). Список закрыт: язык приходит от клиента,
 * и «прочее значение» здесь означало бы письмо, которое некому
 * перевести.
 */
export const LOCALES = ['ru', 'en'];
export const localeSchema = z.enum(LOCALES);
/**
 * Адрес почты.
 *
 * Приводится к нижнему регистру и обрезается по краям сразу в схеме:
 * «Ivan@…» и «ivan@…» — один и тот же ящик, и уникальность в базе
 * стоит по `lower(email)`. Нормализовать в двух местах по-разному —
 * значит однажды завести второй аккаунт на тот же ящик.
 *
 * Длина ограничена сверху не из вежливости: без предела Argon2id
 * получает на вход мегабайт, и это отказ в обслуживании одним
 * запросом.
 */
export const emailSchema = z
    .email('ожидается адрес электронной почты')
    .max(254, 'адрес длиннее 254 знаков не существует')
    .transform((value) => value.trim().toLowerCase());
/**
 * Пароль.
 *
 * Требований к составу нет намеренно: «одна заглавная, одна цифра,
 * один знак» даёт `Password1!` и ничего больше. Длина — единственное,
 * что действительно мешает перебору, поэтому минимум десять знаков.
 *
 * Верхний предел — снова про Argon2id: он считает столько, сколько
 * ему дали, и без предела длинный пароль занимает процесс целиком.
 */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 200;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `пароль короче ${PASSWORD_MIN_LENGTH} знаков`)
    .max(PASSWORD_MAX_LENGTH, `пароль длиннее ${PASSWORD_MAX_LENGTH} знаков`);
/**
 * Токен из письма и refresh-токен: 32 случайных байта в base64url,
 * то есть 43 знака без выравнивания.
 */
export const opaqueTokenSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/, 'токен не той формы');
/** Заголовок, которым клиент предъявляет access-токен. */
export const AUTHORIZATION_HEADER = 'authorization';
/**
 * Пара токенов и сроки их жизни.
 *
 * Сроки приходят вместе с токенами, а не выводятся клиентом
 * из констант: разойтись здесь означает либо разлогин раньше времени,
 * либо запрос с заведомо мёртвым токеном. Время — строка ISO,
 * как и везде на границе.
 */
export const authTokensSchema = z.strictObject({
    accessToken: z.string().min(1),
    accessExpiresAt: z.iso.datetime(),
    refreshToken: opaqueTokenSchema,
    refreshExpiresAt: z.iso.datetime(),
});
/**
 * Профиль владельца сессии.
 *
 * Адрес здесь есть: человеку нужно видеть, в какой аккаунт он вошёл.
 * Это единственное место, где адрес уходит наружу, и уходит он
 * собственному владельцу по HTTPS — в логи и в отчёты мониторинга
 * он не попадает по-прежнему (security-checklist.md, пп. 3 и 6).
 */
export const authProfileSchema = z.strictObject({
    id: uuid,
    email: z.email(),
    locale: localeSchema,
    emailConfirmed: z.boolean(),
});
/**
 * Коды отказа аутентификации.
 *
 * Отдельные коды нужны интерфейсу: «неверный адрес или пароль»
 * и «почта не подтверждена» требуют разных экранов, и различать их
 * по тексту сообщения — это интерфейс, который ломается от правки
 * запятой.
 *
 * Чего здесь нет: кода «такой почты нет». Вход отвечает одинаково
 * на неизвестный адрес и на неверный пароль, а запрос восстановления
 * отвечает «письмо отправлено» в обоих случаях. Разные ответы —
 * это способ перебором узнать, кто у нас зарегистрирован, тот же
 * самый, из-за которого чужой персонаж отвечает «персонажа нет»
 * (`apps/api/src/trpc.ts`).
 */
export const authProblemCodeSchema = z.enum([
    /** Адрес или пароль не подошли. Какое из двух — не говорим. */
    'invalid_credentials',
    /** Пароль верный, но письмо ещё не открыто. */
    'email_not_confirmed',
    /** Токена нет, он просрочен или уже использован — тоже без подробностей. */
    'invalid_token',
    /** Лимит частоты (D36). */
    'rate_limited',
    /** Токен предъявлен, но сессии за ним нет: единственный ответ — выйти. */
    'session_expired',
]);
/**
 * Замечание к полю формы.
 *
 * Отдельно от `authProblemCode`: те коды — про решение сервера
 * («пароль не подошёл», «слишком часто»), а эти — про само значение,
 * которое до решения не доехало. Раньше и то и другое приезжало
 * на экран одним «Не вышло. Попробуйте ещё раз», хотя сервер
 * прекрасно знал, что именно не так.
 *
 * Коды, а не текст сервера: текст сервера английский, а сообщение
 * читает человек на своём языке (D11).
 */
export const validationProblemSchema = z.strictObject({
    /** Поле входа: `email`, `password`. Пустая строка — значение целиком. */
    field: z.string(),
    code: z.enum([
        'invalid_email',
        'password_too_short',
        'password_too_long',
        'required',
        'invalid',
    ]),
});
/**
 * Замечания из ошибки разбора входа.
 *
 * Живёт здесь, а не в сервере, по той же причине, что и схемы:
 * правило «адрес — это адрес, пароль — не короче двенадцати знаков»
 * объявлено один раз, и объяснение нарушения принадлежит тому же
 * месту. Сервер вызывает это с тем, что ему дал tRPC, и не знает
 * ни про zod, ни про форму его ошибок.
 */
export function validationProblemsOf(cause) {
    if (!(cause instanceof z.ZodError))
        return null;
    const problems = cause.issues.map((issue) => {
        const field = issue.path.map(String).join('.');
        if (issue.code === 'too_small') {
            return { field, code: field === 'password' ? 'password_too_short' : 'required' };
        }
        if (issue.code === 'too_big' && field === 'password') {
            return { field, code: 'password_too_long' };
        }
        if (issue.code === 'invalid_format' && field === 'email') {
            return { field, code: 'invalid_email' };
        }
        return { field, code: 'invalid' };
    });
    return problems.length === 0 ? null : problems;
}
export const authProblemSchema = z.strictObject({
    code: authProblemCodeSchema,
    /** Через сколько секунд пробовать снова; только у `rate_limited`. */
    retryAfterSeconds: z.number().int().nonnegative().nullable(),
});
/* ── Регистрация ─────────────────────────────────────────────────── */
export const registerInputSchema = z.strictObject({
    email: emailSchema,
    password: passwordSchema,
    /** Язык писем и интерфейса (D11): берётся из профиля, а не из заголовка. */
    locale: localeSchema,
});
/**
 * Ответ регистрации: токенов нет.
 *
 * Вход до подтверждения почты отклоняется (D8), поэтому выдавать
 * сессию сразу после регистрации незачем — это был бы вход в обход
 * собственной проверки.
 *
 * Ответ одинаков и для свободного адреса, и для занятого. Разные
 * ответы позволяют перебором собрать список зарегистрированных;
 * вместо этого на занятый адрес уходит письмо «на ваш адрес пытались
 * зарегистрироваться», и человек узнаёт ровно то, что должен.
 */
export const registerResultSchema = z.strictObject({
    /** Всегда `true`: значение здесь для формы ответа, а не для ветвления. */
    accepted: z.literal(true),
});
/* ── Вход и выход ────────────────────────────────────────────────── */
export const loginInputSchema = z.strictObject({
    email: emailSchema,
    password: passwordSchema,
});
export const loginResultSchema = z.strictObject({
    tokens: authTokensSchema,
    profile: authProfileSchema,
    /**
     * Драфты этого устройства, которые можно перенести на аккаунт (D33).
     * Приходят вместе со входом, чтобы спросить один раз и сразу:
     * отдельным запросом это был бы второй экран на пустом месте.
     */
    adoptable: z.array(characterSummarySchema),
});
export const refreshInputSchema = z.strictObject({
    refreshToken: opaqueTokenSchema,
});
export const refreshResultSchema = z.strictObject({
    tokens: authTokensSchema,
});
export const logoutInputSchema = z.strictObject({
    refreshToken: opaqueTokenSchema,
    /**
     * Выход со всех устройств: гасит все сессии владельца токена,
     * а не только эту. Признак в теле, а не отдельная процедура —
     * действие одно, различается охват.
     */
    allDevices: z.boolean(),
});
export const logoutResultSchema = z.strictObject({
    /** Сколько живых сессий погашено. Ноль — тоже нормальный ответ. */
    closed: z.number().int().nonnegative(),
});
/* ── Почта и пароль ──────────────────────────────────────────────── */
export const confirmEmailInputSchema = z.strictObject({
    token: opaqueTokenSchema,
});
export const confirmEmailResultSchema = z.strictObject({
    /**
     * Подтверждение не входит в аккаунт: токенов в ответе нет. Ссылка
     * из письма, дающая сессию, — это сессия каждому, кто прочтёт ящик.
     */
    confirmed: z.literal(true),
});
/**
 * Повторная отправка письма с подтверждением (D41).
 *
 * Нужна потому, что письмо больше не уходит внутри регистрации:
 * оно ставится в очередь, и между «аккаунт создан» и «письмо в ящике»
 * появился зазор. Зазор бывает и вечным — провайдер лежит, письмо
 * ушло в спам, — и единственное, что человек может сделать сам, это
 * попросить ещё раз.
 *
 * Ответ, как и у восстановления пароля, одинаков во всех случаях:
 * адреса нет, адрес есть и подтверждён, адрес есть и не подтверждён.
 * Разные ответы — способ перебором собрать список зарегистрированных.
 */
export const resendConfirmationInputSchema = z.strictObject({
    email: emailSchema,
});
export const resendConfirmationResultSchema = z.strictObject({
    /** Всегда `true`: см. комментарий к схеме входа. */
    accepted: z.literal(true),
});
export const requestPasswordResetInputSchema = z.strictObject({
    email: emailSchema,
});
export const requestPasswordResetResultSchema = z.strictObject({
    /** Всегда `true`, зарегистрирован адрес или нет: см. `authProblemCodeSchema`. */
    accepted: z.literal(true),
});
export const resetPasswordInputSchema = z.strictObject({
    token: opaqueTokenSchema,
    password: passwordSchema,
});
export const resetPasswordResultSchema = z.strictObject({
    /**
     * Смена пароля гасит все сессии: если пароль меняют потому, что его
     * увели, оставить чужую сессию живой означает не сменить ничего.
     */
    closed: z.number().int().nonnegative(),
});
/* ── Драфты и аккаунт (D33) ──────────────────────────────────────── */
export const adoptDraftsInputSchema = z.strictObject({
    /**
     * Явный список, а не «все драфты устройства»: за столом один ноутбук
     * на партию, и переносить чужое молча нельзя (D33).
     */
    characterIds: z.array(uuid).min(1).max(50),
});
export const adoptDraftsResultSchema = z.strictObject({
    characters: z.array(characterSummarySchema),
});
/** Кто вошёл: ответ на «а я вообще залогинен». */
export const meResultSchema = z.strictObject({
    profile: authProfileSchema,
});
//# sourceMappingURL=schemas.js.map